'use client';

import { useAnalytics } from '@repo/analytics/analytics-provider';
import { identifyClarity, setClarityTags, trackClarityEvent } from '@repo/analytics/clarity';
import { useClarityState } from '@repo/analytics/clarity-state';
import { useHostMode, useHostType, useRpcType } from '@repo/host/hooks/use-host-info';
import { css } from '@repo/panda/css';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { refreshFeatureFlags } from '@repo/store/maps/slices/featureFlags';
import { selectExternalMapId, selectFogOfWarEnabled, selectMapData, selectPlayerBearing, setFollowEnabled } from '@repo/store/maps/slices/mapData';
import { HostMode, HostType } from '@repo/types/host';
import type { MapInitializationData } from '@repo/types/maps/map-types';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MiniMapLaunchProvider } from '../../mini-map-cta/mini-map-launch-context';
import { EmbedLimitModal } from '../embed-limit-modal';
import { PIN_INTERACTION_LAYER_IDS } from '../hooks/constants';
import { useCustomPinUrlHandler } from '../hooks/useCustomPinUrlHandler';
import { useGameMap } from '../hooks/useGameMap';
import { useGameMapUserData } from '../hooks/useGameMapUserData';
import { useLocalMapPreviewData } from '../hooks/useLocalMapPreviewData';
import { useMapPinOperations } from '../hooks/useMapPinOperations';
import { useMapPreferencesSync } from '../hooks/useMapPreferencesSync';
import { useMobileViewportDetection } from '../hooks/useMobileViewportDetection';
import { useOverlayMapContextSync } from '../hooks/useOverlayMapContextSync';
import { usePlayerLocation } from '../hooks/usePlayerLocation';
import { useMapContext } from '../map-context';
import { GameMapLoading } from './game-map-loading';
import { PreferencesConflictModal } from './preferences-conflict-modal';
import 'maplibre-gl/dist/maplibre-gl.css';
import { getHost, supportsHostAuthToken } from '@repo/host/host';
import { useGetUserQuery } from '@repo/store/maps/api/authApi';
import { selectAuthToken, selectIsMapEditor } from '@repo/store/maps/slices/auth';
import { selectIsMiniMapMode, updateMiniMapMode } from '@repo/store/maps/slices/clientContext';
import { clearFog, selectIsInCreatorMode, setFilterStateFromUrl, toggleCreatorMode } from '@repo/store/maps/slices/mapData';
import type { GameSessionInternal } from '@repo/types/sessions/session-types';
import { MapsAuthModal } from '../../components/maps-auth-modal';
import { FogContextMenu } from '../../fog-of-war/fog-context-menu';
import { FogResetConfirmModal } from '../../fog-of-war/fog-reset-confirm-modal';
import { fogSpatialIndex } from '../../fog-of-war/fog-spatial-index';
import { useFogContextMenu } from '../../fog-of-war/useFogContextMenu';
import { LocationHoverTooltip } from '../../location-info/location-hover-tooltip';
import { hasAuthTokenCookie } from '../../utils/auth-cookie';
import { getIsConfiguredMiniMap, getIsDedicatedMiniMap, getIsSessionMiniMap } from '../../utils/mini-map-mode';
import { useAutoLoadSession } from '../hooks/useAutoLoadSession';
import { useRegionContextMenu } from '../hooks/useRegionContextMenu';
import { SessionHistoryProvider } from '../hooks/useSessionHistory';
import { RegionContextMenu } from './region-context-menu';
const LazyObject3dLayer = lazy(() => import('./object-3d-layer').then(mod => ({
  default: mod.Object3dLayer
})));
const MAP_Z_INDEX = {
  container: -1,
  hoverTooltip: 3
} as const;
interface GameMapClientProps {
  providedMapData: MapInitializationData;
  children: React.ReactNode;
}
function AutoLoadSessionHandler(t0) {
  const {
    authenticatedSessionReady
  } = t0;
  useAutoLoadSession(authenticatedSessionReady);
  return null;
}
export function GameMapClient({
  providedMapData,
  children
}: GameMapClientProps) {
  const hostType = useHostType();
  const hostMode = useHostMode();
  const rpcType = useRpcType();
  const dispatch = useAppDispatch();
  const analytics = useAnalytics();
  const clarity = useClarityState();
  const mapData = useAppSelector(selectMapData);
  const externalMapId = useAppSelector(selectExternalMapId);
  const fogEnabled = useAppSelector(selectFogOfWarEnabled);
  const isInCreatorMode = useAppSelector(selectIsInCreatorMode);
  const authToken = useAppSelector(selectAuthToken);
  const isMapEditor = useAppSelector(selectIsMapEditor);
  const activeMapData = useLocalMapPreviewData(providedMapData, isMapEditor);
  const isLocalPreviewLoading = !activeMapData;
  const isMiniMapMode = useAppSelector(selectIsMiniMapMode);
  const playerBearing = useAppSelector(selectPlayerBearing);
  const expectedMiniMapMode = getIsConfiguredMiniMap(providedMapData.mapConfig);
  const isDedicatedMiniMap = getIsDedicatedMiniMap(providedMapData.mapConfig);
  const {
    isReady: prefsReady,
    hasStoredPreferences,
    onRemoteMarkerSelectRef,
    sendMessageRef,
    requestMiniMapOpen
  } = useMapPreferencesSync({
    syncSelectedMarker: !expectedMiniMapMode
  });
  const {
    selectMarkerInMap,
    clearSelectedMarkerInMap
  } = useMapPinOperations();
  const {
    mapContainerRef,
    isMapInitialized,
    popupDivRef,
    hoveredMarker,
    pendingUrlFilterState,
    cleanUpFilterUrlParams,
    isPinLoginModalOpen,
    setIsPinLoginModalOpen,
    isFogLoginModalOpen,
    setIsFogLoginModalOpen,
    fogReady,
    fogRevealAtLatLng
  } = useGameMap(activeMapData, {
    hasStoredPreferences,
    syncSettled: prefsReady,
    sendMessageRef
  });
  const [isEmbedLimitModalOpen, setIsEmbedLimitModalOpen] = useState(false);
  const {
    map
  } = useMapContext();
  const [pinClickCount, setPinClickCount] = useState(0);
  const isEmbed = hostMode === HostMode.EMBED && hostType === HostType.WEB;
  const canUseHostAuth = supportsHostAuthToken({
    type: hostType,
    mode: hostMode,
    rpc: rpcType
  });
  const currentMapInfo = useMemo(() => providedMapData.mapInfo.find(map_0 => map_0.externalId === providedMapData.mapConfig.externalId), [providedMapData.mapConfig.externalId, providedMapData.mapInfo]);
  const clarityMapPageId = `maps:${providedMapData.mapConfig.externalId}`;
  const isSessionMiniMap = getIsSessionMiniMap(providedMapData.mapConfig, isMiniMapMode);
  const [showPrefsConflictModal, setShowPrefsConflictModal] = useState(false);
  const [miniMapFollowFacing, setMiniMapFollowFacing] = useState(false);
  const clarityMapViewKeyRef = useRef<string | null>(null);
  const {
    isSuccess: authenticationSucceeded,
    isFetching: authenticationPending
  } = useGetUserQuery(undefined, {
    skip: !isEmbed && !canUseHostAuth && !authToken && !hasAuthTokenCookie()
  });
  // A skipped query means there is no available auth source, so private session loading remains blocked.
  const authenticatedSessionReady = authenticationSucceeded && !authenticationPending;
  const [timelineVisible, setTimelineVisible] = useState(false);
  const [currentSession, setCurrentSession] = useState<GameSessionInternal | null>(null);
  const showSessionTrail = useCallback((session: GameSessionInternal) => {
    setCurrentSession(session);
  }, []);
  const hideSessionTrail = useCallback(() => {
    setCurrentSession(null);
    setTimelineVisible(false);
  }, []);
  const getCurrentSession = useCallback(() => {
    return currentSession;
  }, [currentSession]);
  useMobileViewportDetection();
  useGameMapUserData();
  usePlayerLocation(sendMessageRef, fogRevealAtLatLng, {
    lockToPlayer: isDedicatedMiniMap
  });
  useOverlayMapContextSync();
  useEffect(() => {
    if (!expectedMiniMapMode || !isMapInitialized || !map) return;
    const metadata = activeMapData?.mapData.metadata;
    const minimumZoom = metadata?.minZoom ?? map.getMinZoom();
    const maximumZoom = Math.min(metadata?.maxZoom ?? map.getMaxZoom(), map.getMaxZoom());
    const preferredZoom = Math.min(maximumZoom, Math.max(minimumZoom, (metadata?.initialZoom ?? minimumZoom) + 3));
    dispatch(setFollowEnabled(true));
    map.setZoom(preferredZoom);
  }, [activeMapData?.mapData.metadata, dispatch, expectedMiniMapMode, isMapInitialized, map]);
  useEffect(() => {
    if (canUseHostAuth && !authToken) return;
    void dispatch(refreshFeatureFlags(authToken));
  }, [authToken, canUseHostAuth, dispatch]);
  useEffect(() => {
    if (!clarity.enabled) {
      clarityMapViewKeyRef.current = null;
      return;
    }
    identifyClarity(clarity.accountUuid, {
      pageId: clarityMapPageId
    });
    setClarityTags({
      surface: 'maps',
      page_id: clarityMapPageId,
      map_id: providedMapData.mapConfig.externalId,
      map_name: currentMapInfo?.name ?? providedMapData.mapData.metadata.name,
      map_slug: currentMapInfo?.slug,
      title_id: providedMapData.mapConfig.titleId,
      title_name: providedMapData.mapConfig.titleName,
      title_slug: providedMapData.mapConfig.titleSlug,
      host_type: hostType,
      host_mode: hostMode,
      rpc_type: rpcType,
      auth_state: clarity.accountUuid || authToken || hasAuthTokenCookie() ? 'authenticated' : 'anonymous',
      mini_map_mode: isMiniMapMode,
      session_map: !!(providedMapData.mapConfig.sessionUuid || providedMapData.mapConfig.publicSessionId),
      debug_map: providedMapData.mapConfig.debug,
      creator_mode: isInCreatorMode
    });
    const mapViewKey = `${hostType}:${hostMode}:${providedMapData.mapConfig.externalId}`;
    if (clarityMapViewKeyRef.current !== mapViewKey) {
      clarityMapViewKeyRef.current = mapViewKey;
      trackClarityEvent('maps_game_map_view');
    }
  }, [authToken, clarity.accountUuid, clarity.enabled, clarityMapPageId, currentMapInfo?.name, currentMapInfo?.slug, hostMode, hostType, isInCreatorMode, isMiniMapMode, providedMapData.mapConfig.debug, providedMapData.mapConfig.externalId, providedMapData.mapConfig.publicSessionId, providedMapData.mapConfig.sessionUuid, providedMapData.mapConfig.titleId, providedMapData.mapConfig.titleName, providedMapData.mapConfig.titleSlug, providedMapData.mapData.metadata.name, rpcType]);
  useEffect(() => {
    if (expectedMiniMapMode) {
      clearSelectedMarkerInMap();
    }
  }, [clearSelectedMarkerInMap, expectedMiniMapMode]);
  useEffect(() => {
    onRemoteMarkerSelectRef.current = (markerId: string | null) => {
      if (expectedMiniMapMode || isSessionMiniMap) {
        return;
      }
      if (markerId) {
        selectMarkerInMap(markerId, {
          flyTo: true
        });
      } else {
        clearSelectedMarkerInMap();
      }
    };
    return () => {
      onRemoteMarkerSelectRef.current = null;
    };
  }, [onRemoteMarkerSelectRef, selectMarkerInMap, clearSelectedMarkerInMap, expectedMiniMapMode, isSessionMiniMap]);
  useEffect(() => {
    if (pendingUrlFilterState) {
      setShowPrefsConflictModal(true);
    }
  }, [pendingUrlFilterState]);
  const handleUseLinkFilters = useCallback(() => {
    if (pendingUrlFilterState) {
      dispatch(setFilterStateFromUrl(pendingUrlFilterState));
    }
    cleanUpFilterUrlParams();
    setShowPrefsConflictModal(false);
  }, [dispatch, pendingUrlFilterState, cleanUpFilterUrlParams]);
  const handleKeepMyFilters = useCallback(() => {
    cleanUpFilterUrlParams();
    setShowPrefsConflictModal(false);
  }, [cleanUpFilterUrlParams]);
  const {
    isLoginModalOpen: isCustomPinLoginModalOpen,
    setIsLoginModalOpen: setIsCustomPinLoginModalOpen
  } = useCustomPinUrlHandler(isMapInitialized);
  const {
    menuState: regionMenuState,
    close: closeRegionMenu,
    toggleRegionPins,
    showOnlyRegion,
    bulkUpdateRegionPins,
    isLoginModalOpen: isRegionLoginModalOpen,
    setIsLoginModalOpen: setIsRegionLoginModalOpen
  } = useRegionContextMenu();
  const {
    menuState: fogMenuState,
    closeMenu: closeFogMenu,
    openConfirm: openFogResetConfirm,
    isConfirmOpen: isFogResetConfirmOpen,
    closeConfirm: closeFogResetConfirm
  } = useFogContextMenu();
  const handleConfirmFogReset = useCallback(() => {
    fogSpatialIndex.clear();
    dispatch(clearFog());
    sendMessageRef.current?.({
      type: 'fog_clear'
    });
    if (analytics && externalMapId && hostType) {
      analytics.event('game_map_reset_fog_confirm', {
        mapId: externalMapId,
        platform: hostType
      });
    }
    closeFogResetConfirm();
  }, [analytics, externalMapId, hostType, dispatch, sendMessageRef, closeFogResetConfirm]);

  // Auto-enable creator mode once per load if debug=true is present
  const hasAutoEnabledRef = useRef(false);
  useEffect(() => {
    if (hasAutoEnabledRef.current) return;
    const debugFromUrl = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('debug') === 'true' : false;
    const localPreviewFromUrl = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('localPreview') === 'true' : false;
    const debugFromConfig = Boolean(providedMapData.mapConfig.debug);
    const shouldEnableCreator = (debugFromUrl || debugFromConfig) && !localPreviewFromUrl;
    if (shouldEnableCreator && isMapEditor && !isInCreatorMode) {
      dispatch(toggleCreatorMode());
      hasAutoEnabledRef.current = true;
    }
  }, [dispatch, isMapEditor, isInCreatorMode, providedMapData.mapConfig.debug]);
  useEffect(() => {
    const handleMarkerClick = () => {
      setPinClickCount(prev => prev + 1);
    };
    if (hostMode === HostMode.EMBED) {
      map?.on('click', [...PIN_INTERACTION_LAYER_IDS], handleMarkerClick);
    }
    return () => {
      map?.off('click', [...PIN_INTERACTION_LAYER_IDS], handleMarkerClick);
    };
  }, [hostMode, map]);
  useEffect(() => {
    if (pinClickCount >= 5 && isEmbed) {
      setIsEmbedLimitModalOpen(true);
      setPinClickCount(0);
    }
  }, [pinClickCount, isEmbed]);
  useEffect(() => {
    const subscription = getHost().onSetMinimapMode(isMiniMapMode_0 => {
      dispatch(updateMiniMapMode(isMiniMapMode_0));
    });
    return () => subscription.dispose();
  }, [dispatch]);
  useEffect(() => {
    const subscription_0 = getHost().onSetMinimapFollowFacing(setMiniMapFollowFacing);
    return () => subscription_0.dispose();
  }, []);
  useEffect(() => {
    if (!expectedMiniMapMode || !map) return;
    map.easeTo({
      bearing: miniMapFollowFacing && playerBearing !== null ? playerBearing : 0,
      duration: 300,
      easing: t => t
    });
  }, [expectedMiniMapMode, map, miniMapFollowFacing, playerBearing]);

  // Sync minimap mode from mapConfig (e.g., from URL params in post-session dialog)
  const isMapConfigSyncedRef = useRef(false);
  if (!isMapConfigSyncedRef.current && isMiniMapMode === expectedMiniMapMode) {
    isMapConfigSyncedRef.current = true;
  }
  const isMapConfigSynced = isMapConfigSyncedRef.current;
  useEffect(() => {
    dispatch(updateMiniMapMode(expectedMiniMapMode));
  }, [dispatch, expectedMiniMapMode]);
  const sessionTrailsValue = useMemo(() => ({
    showSessionTrail,
    hideSessionTrail,
    getCurrentSession,
    timelineVisible,
    setTimelineVisible,
    currentSession,
    setCurrentSession
  }), [showSessionTrail, hideSessionTrail, getCurrentSession, timelineVisible, currentSession]);
  return <SessionHistoryProvider sessionTrails={sessionTrailsValue}>
            <AutoLoadSessionHandler authenticatedSessionReady={authenticatedSessionReady} />
            {mapData?.objects3d?.features && mapData.objects3d.features.length > 0 && <Suspense fallback={null}>
                    <LazyObject3dLayer />
                </Suspense>}
            {(isLocalPreviewLoading || !mapData || !isMapConfigSynced || !prefsReady || !fogReady) && <GameMapLoading />}

            <div id="map" ref={mapContainerRef} data-fog-loading={fogEnabled && !fogReady ? '' : undefined} style={{
      background: mapData?.metadata.backgroundColor ?? activeMapData?.mapData.metadata.backgroundColor ?? undefined,
      overscrollBehavior: 'none'
    }} className={css({
      position: 'relative',
      width: 'full',
      height: 'full',
      transform: 'translateZ(0)',
      // Ensure GPU acceleration
      zIndex: MAP_Z_INDEX.container,
      '&[data-fog-loading] .maplibregl-canvas': {
        filter: '[blur(4px)]'
      },
      // Global MapLibre popup styles
      '& .maplibregl-popup-content': {
        padding: '0 !important',
        background: '[none !important]'
      },
      '& .maplibregl-popup-tip': {
        display: 'none !important'
      },
      '& .maplibregl-popup': {
        pointerEvents: 'auto !important'
      },
      '& .maplibregl-popup.location-hover-tooltip': {
        zIndex: MAP_Z_INDEX.hoverTooltip
      }
    })} />

            {isMapConfigSynced && prefsReady && activeMapData && <MiniMapLaunchProvider launch={requestMiniMapOpen}>{children}</MiniMapLaunchProvider>}

            <MapsAuthModal isOpen={isCustomPinLoginModalOpen} onOpenChange={details => setIsCustomPinLoginModalOpen(details.open)} trigger="custom-pin-url" size="sm" />
            <MapsAuthModal isOpen={isRegionLoginModalOpen} onOpenChange={details_0 => setIsRegionLoginModalOpen(details_0.open)} trigger="mark-found" size="sm" />
            <MapsAuthModal isOpen={isPinLoginModalOpen} onOpenChange={details_1 => setIsPinLoginModalOpen(details_1.open)} trigger="mark-found" size="sm" />
            <MapsAuthModal isOpen={isFogLoginModalOpen} onOpenChange={details_2 => setIsFogLoginModalOpen(details_2.open)} trigger="fog-of-war" size="sm" />

            <EmbedLimitModal isOpen={isEmbedLimitModalOpen} onOpenChange={() => setIsEmbedLimitModalOpen(false)} />

            <PreferencesConflictModal isOpen={showPrefsConflictModal} onUseLinkFilters={handleUseLinkFilters} onKeepMyFilters={handleKeepMyFilters} />

            {regionMenuState.isOpen && <RegionContextMenu key={`${regionMenuState.regionId}-${regionMenuState.x}-${regionMenuState.y}`} x={regionMenuState.x} y={regionMenuState.y} regionTitle={regionMenuState.regionTitle} isRegionHidden={regionMenuState.isRegionHidden} onToggle={toggleRegionPins} onShowOnly={showOnlyRegion} onMarkAllFound={() => bulkUpdateRegionPins(true)} onMarkAllUnfound={() => bulkUpdateRegionPins(false)} onClose={closeRegionMenu} />}

            {fogMenuState.isOpen && <FogContextMenu key={`fog-${fogMenuState.x}-${fogMenuState.y}`} x={fogMenuState.x} y={fogMenuState.y} onReset={openFogResetConfirm} onClose={closeFogMenu} />}

            <FogResetConfirmModal isOpen={isFogResetConfirmOpen} onConfirm={handleConfirmFogReset} onCancel={closeFogResetConfirm} />

            {popupDivRef.current && hoveredMarker && createPortal(<LocationHoverTooltip marker={hoveredMarker} />, popupDivRef.current)}
        </SessionHistoryProvider>;
}